import { $height, $offset } from 'fxdom/es';
import Headroom from 'headroom.js';
import { isSafari } from '../../../../services/MarppleShop/shared/util/ua.ts';
/**
 * @description
 * 이 클래스는 두 개의 헤더를 관리하는 클래스입니다.
 * - primary_header: 메인 헤더
 * - secondary_header: 서브 헤더
 *
 * @reference
 * [헤더 트랜지션 버그 핸들링 문서](https://www.notion.so/marpple/nbox-2f09e89f95864ff0b0dc7ea8356efb53?pvs=4)
 *  - headroom 라이프사이클에서 두개 이상의 headroom을 사용할 때 발생하는 트랜지션 버그를 해결하기 위한 문서
 */
export class DualHeaderScrollManager {
  /**
   * @typedef {Object} ScrollEventCallback
   * @property {Function} onTop
   * @property {Function} onNotTop
   * @property {Function} onUnpin
   * @property {Function} onPin
   * @property {Function} onBottom
   * @property {Function} onNotBottom
   */

  /**
   * @type {HTMLElement}
   */
  primary_header;

  /**
   * @type {HTMLElement}
   */
  secondary_header;

  /**
   * @type {string}
   */
  toggle_class;

  /**
   * @type {ScrollEventCallback}
   */
  primary_header_scroll_events;

  /**
   * @type {ScrollEventCallback}
   */
  secondary_header_scroll_events;

  #is_secondary_header_on_top = false;

  /**
   * @type {Headroom}
   */
  #primary_headroom;

  /**
   * @type {Headroom}
   */
  #secondary_headroom;

  /**
   * @typedef {Object} DualHeaderScrollManagerArgs
   * @property {HTMLElement} primary_header
   * @property {HTMLElement} secondary_header
   * @property {string} toggle_class
   * @property {ScrollEventCallback} primary_header_scroll_events
   * @property {ScrollEventCallback} secondary_header_scroll_events
   * @param {DualHeaderScrollManagerArgs} args
   */
  constructor(args) {
    this.primary_header = args.primary_header;
    this.secondary_header = args.secondary_header;
    this.toggle_class = args.toggle_class;
    this.primary_header_scroll_events = args.primary_header_scroll_events;
    this.secondary_header_scroll_events = args.secondary_header_scroll_events;
  }

  initialize() {
    const offset = this.#calculateOffset(this.primary_header, this.secondary_header);

    this.#primary_headroom = new Headroom(this.primary_header, {
      offset,
      ...this.primary_header_scroll_events,
    }).init();

    this.#secondary_headroom = new Headroom(this.secondary_header, {
      offset,
      ...this.secondary_header_scroll_events,
      onUnpin: () => {
        if (this.#is_secondary_header_on_top) {
          this.#secondary_headroom.pin();
        }
        this.secondary_header_scroll_events?.onUnpin?.();
      },
      onTop: () => {
        requestAnimationFrame(() => {
          this.#is_secondary_header_on_top = true;
          this.secondary_header.classList.remove(this.toggle_class);
        });

        this.#primary_headroom.freeze();
        this.secondary_header_scroll_events?.onTop?.();
      },
      onNotTop: () => {
        this.#primary_headroom.unfreeze();

        requestAnimationFrame(() => {
          this.#is_secondary_header_on_top = false;
          this.secondary_header.classList.add(this.toggle_class);
        });

        this.secondary_header_scroll_events?.onNotTop?.();
      },
    }).init();
  }

  /**
   * @returns {boolean}
   */
  get isInitialized() {
    return this.#primary_headroom && this.#secondary_headroom;
  }

  /**
   *
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   * @returns {number}
   */
  #calculateOffset(source, target) {
    const offset = $offset(target).top - $height(source);
    // offset이 음수 일 때 safari에서 scrollY가 0일 때 최상단에서 더 위로 스크롤 시도시 unpinned가 되는 문제가 있어 0으로 설정
    return isSafari() ? Math.max(0, offset) : offset;
  }
}
